import {
    createContext, useContext
} from 'react';
import {
    KBContext
} from './KBGlobalContext';

/**
 * @typedef {Object} DocumentSectionContextType
 * @property {Set<string>} selectedDocs - Set of selected document IDs
 * @property {Function} setSelectedDocs - used to set the selected docs from the table
 * @property {function(string): void} toggleDocSelection - Toggle document selection
 * @property {function(): void} handleSelectAll - Handle select all documents
 * @property {function(string): boolean} isSelected - Check if document is selected
 * @property {function(): boolean} isAllSelected - Check if all documents are selected
 * @property {string} searchQuery - Current search query
 * @property {function(string): void} setSearchQuery - Set search query
 * @property {function(): void} searchCurrentFolder - Search in current folder
 * @property {function(): void} resetFilteredDocs - Resets the filtered docs if there are results regardless.
 * @property {function(): string[]} getVisibleDocs - Get visible documents
 * @property {boolean} showSearchResultComponent - Visibility of the "Search results for: "
 * @property {function(): void} setShowSearchResultComponent - Setter for the visibility of showSearchResultComponent
 * @property {string} searchName - The last search
 * @property {boolean} documentIsLoading True if the document list is loading.
 */

/**
 * Context for managing document section state
 * @type {import('react').Context<DocumentSectionContextType>}
 */
export const DocumentSectionContext = createContext({
    selectedDocs: new Set(),
    setSelectedDocs: () => {},
    toggleDocSelection: () => {},
    handleSelectAll: () => {},
    isSelected: () => false,
    isAllSelected: () => false,
    searchQuery: '',
    setSearchQuery: () => {},
    searchCurrentFolder: () => {},
    getVisibleDocs: () => [],
    showSearchResultComponent: false,
    setShowSearchResultComponent: () => {},
    resetFilteredDocs: () => {},
    searchName: '',
    documentIsLoading: false,
});

/**
 * Provider component for Document Section context
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children Child components
 */
export function DocumentSectionProvider({
    children
}) {
    const {
        tableHandler, documentIsLoading
    } = useContext(KBContext);

    return (
        <DocumentSectionContext.Provider
            value={{
                selectedDocs: tableHandler.selectedDocs,
                setSelectedDocs: tableHandler.setSelectedDocs,
                toggleDocSelection: tableHandler.toggleDocSelection,
                handleSelectAll: tableHandler.handleSelectAll,
                isSelected: tableHandler.isSelected,
                isAllSelected: tableHandler.isAllSelected,
                searchQuery: tableHandler.searchQuery,
                setSearchQuery: tableHandler.setSearchQuery,
                searchCurrentFolder: tableHandler.searchCurrentFolder,
                getVisibleDocs: tableHandler.getVisibleDocs,
                showSearchResultComponent: tableHandler.showSearchResultComponent,
                setShowSearchResultComponent: tableHandler.setShowSearchResultComponent,
                resetFilteredDocs: tableHandler.resetFilteredDocs,
                searchName: tableHandler.searchName,
                documentIsLoading,
            }}
        >
            {children}
        </DocumentSectionContext.Provider>
    );
}
